import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { NotAuthenticatedError } from './AxiosWrapper';
import { isFacebookInAppBrowserBridgeError } from './facebookInAppBrowser';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN ?? '',
  // NotAuthenticatedError is routine auth control flow (a lapsed session), not
  // a defect. The query/mutation reporter (reportApiError.ts) already filters
  // it, but imperative callers — an un-awaited callProtectedApi, or
  // BrandingProvider capturing it directly in a catch — bypass that filter and
  // would otherwise file noise. Drop it globally by TYPE (not a substring
  // message match, which a reworded/coincidental message would silently break)
  // so a routine re-auth never becomes a Sentry issue from any path.
  //
  // Facebook's in-app browser throws inside its own injected native bridge on pages it
  // renders. Those frames are all Facebook's, so no tach-ui change can fix them — drop
  // them here rather than leave a standing issue nobody can action. See
  // `facebookInAppBrowser.ts` for why this matches Facebook's own bridge signals rather
  // than the browser name.
  beforeSend(event, hint) {
    if (hint?.originalException instanceof NotAuthenticatedError) {
      return null;
    }
    if (isFacebookInAppBrowserBridgeError(event)) {
      return null;
    }
    return event;
  },
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
    // Only enable profiling in production
    ...(process.env.NODE_ENV === 'production'
      ? [Sentry.browserProfilingIntegration()]
      : []),
  ],
  // NODE_ENV (not REACT_APP_ENV): this was the effective value before the
  // init consolidation, and Sentry alert filters key on environment:production.
  // Switching to REACT_APP_ENV ('prod'/'staging') needs coordinated filter
  // updates in Sentry first.
  environment: process.env.NODE_ENV ?? 'staging',
  initialScope: {
    tags: { service: 'tach-ui' },
  },
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,
  // Only enable profiling in production
  profilesSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    'localhost',
    /^\//,
    /^https:\/\/api-staging\.tachmoney\.com\/api/,
    /^https:\/\/api-prod\.tachusa\.com\/api/,
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
